import React, { useState } from 'react';
import { useGetIdentity, useNotify } from 'react-admin';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Separator } from '../components/ui/separator';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { User2, Lock, Monitor, LogOut, Shield, Globe } from 'lucide-react';
import { toastSuccess, toastError } from '../lib/toast';
import { cn } from '../lib/utils';

/* ============================================================
   Account — Latio design system (derived). Profile / security / preferences.
   (Profile/password/sessions are stubbed pending backend endpoints.)
   ============================================================ */

interface Session {
  id: string;
  device: string;
  location: string;
  lastActive: string;
  current: boolean;
}

const MOCK_SESSIONS: Session[] = [
  { id: '1', device: 'Chrome on macOS', location: 'Medellín, CO', lastActive: 'Now', current: true },
  { id: '2', device: 'Safari on iPhone', location: 'Medellín, CO', lastActive: '2 hours ago', current: false },
];

function SectionCard({
  title,
  icon: Icon,
  action,
  children,
}: {
  title: string;
  icon: React.ElementType;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-card border border-hairline bg-background p-5">
      <div className="mb-4 flex items-center justify-between gap-3">
        <h3 className="flex items-center gap-2 text-[15px] font-bold text-ink">
          <Icon className="size-4 text-ink-2" />
          {title}
        </h3>
        {action}
      </div>
      {children}
    </div>
  );
}

function PrimaryBtn({ onClick, disabled, children }: { onClick: () => void; disabled?: boolean; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="inline-flex h-9 items-center gap-2 rounded-pill bg-black px-4 text-sm font-semibold text-white hover:bg-ink disabled:opacity-50"
    >
      {children}
    </button>
  );
}

export function AccountPage() {
  const { data: identity } = useGetIdentity();
  const notify = useNotify();

  const [displayName, setDisplayName] = useState(identity?.fullName || '');
  const [savingProfile, setSavingProfile] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [savingPassword, setSavingPassword] = useState(false);
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [darkMode, setDarkMode] = useState(false);
  const [compactView, setCompactView] = useState(false);

  const handleSaveProfile = async () => {
    setSavingProfile(true);
    try {
      await new Promise((r) => setTimeout(r, 600));
      toastSuccess('Profile updated');
    } catch {
      toastError('Failed to update profile');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleChangePassword = async () => {
    if (!currentPassword || !newPassword || !confirmPassword) return notify('All fields are required', { type: 'warning' });
    if (newPassword !== confirmPassword) return toastError('Passwords do not match');
    if (newPassword.length < 8) return notify('Password must be at least 8 characters', { type: 'warning' });
    setSavingPassword(true);
    try {
      await new Promise((r) => setTimeout(r, 600));
      toastSuccess('Password changed');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch {
      toastError('Failed to change password');
    } finally {
      setSavingPassword(false);
    }
  };

  const handleLogoutAllSessions = async () => {
    try {
      await new Promise((r) => setTimeout(r, 400));
      toastSuccess('All other sessions have been logged out');
    } catch {
      toastError('Failed to logout sessions');
    }
  };

  const toggleRow = (label: string, description: string, checked: boolean, onChange: (v: boolean) => void) => (
    <div className="flex items-center justify-between">
      <div>
        <Label className="text-sm font-medium text-ink">{label}</Label>
        <p className="text-[12px] text-ink-3">{description}</p>
      </div>
      <Switch checked={checked} onCheckedChange={onChange} />
    </div>
  );

  return (
    <div className="mx-auto max-w-2xl space-y-6 p-6">
      <div>
        <h1 className="text-[24px] font-bold leading-none text-ink" style={{ letterSpacing: '-0.02em' }}>
          Account
        </h1>
        <p className="mt-1 text-sm text-ink-2">Manage your profile, security, and preferences.</p>
      </div>

      <SectionCard title="Profile" icon={User2}>
        <div className="space-y-4">
          <div className="flex items-center gap-4">
            <span className="flex size-16 select-none items-center justify-center rounded-pill bg-indigo-tint text-lg font-bold text-indigo">
              {(identity?.fullName || 'A').charAt(0).toUpperCase()}
            </span>
            <div>
              <p className="text-sm font-semibold text-ink">{identity?.fullName || 'Admin User'}</p>
              <p className="text-[12px] text-ink-3">{identity?.id || ''}</p>
              <Badge variant="secondary" className="mt-1 text-xs">Admin</Badge>
            </div>
          </div>
          <Separator />
          <div className="space-y-2">
            <Label htmlFor="display-name">Display name</Label>
            <Input id="display-name" value={displayName} onChange={(e) => setDisplayName(e.target.value)} placeholder="Your name" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input id="email" value={identity?.id || ''} disabled className="bg-surface-2" />
            <p className="text-[12px] text-ink-3">Email cannot be changed here. Contact a super admin.</p>
          </div>
          <div className="flex justify-end">
            <PrimaryBtn onClick={handleSaveProfile} disabled={savingProfile}>
              {savingProfile ? 'Saving…' : 'Save changes'}
            </PrimaryBtn>
          </div>
        </div>
      </SectionCard>

      <SectionCard title="Change password" icon={Lock}>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="current-password">Current password</Label>
            <Input id="current-password" type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} placeholder="••••••••" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="new-password">New password</Label>
            <Input id="new-password" type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="••••••••" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="confirm-password">Confirm new password</Label>
            <Input id="confirm-password" type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder="••••••••" />
          </div>
          <div className="flex justify-end">
            <PrimaryBtn onClick={handleChangePassword} disabled={savingPassword}>
              {savingPassword ? 'Updating…' : 'Update password'}
            </PrimaryBtn>
          </div>
        </div>
      </SectionCard>

      <SectionCard title="Preferences" icon={Monitor}>
        <div className="space-y-4">
          {toggleRow('Dark mode', 'Switch to a dark color scheme', darkMode, setDarkMode)}
          <Separator />
          {toggleRow('Email notifications', 'Receive alerts and reports by email', emailNotifications, setEmailNotifications)}
          <Separator />
          {toggleRow('Compact view', 'Reduce table row height for denser data', compactView, setCompactView)}
        </div>
      </SectionCard>

      <SectionCard
        title="Active sessions"
        icon={Shield}
        action={
          <button
            type="button"
            onClick={handleLogoutAllSessions}
            className="inline-flex h-8 items-center gap-1.5 rounded-pill border border-error/30 px-3 text-[13px] font-semibold text-error hover:bg-error-bg"
          >
            <LogOut className="size-3.5" />
            Logout all others
          </button>
        }
      >
        <div className="space-y-3">
          {MOCK_SESSIONS.map((session, index) => (
            <React.Fragment key={session.id}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="flex size-8 items-center justify-center rounded-latio-sm bg-surface-2">
                    <Globe className="size-4 text-ink-2" />
                  </span>
                  <div>
                    <p className="text-sm font-medium text-ink">{session.device}</p>
                    <p className="text-[12px] text-ink-3">{session.location} · {session.lastActive}</p>
                  </div>
                </div>
                {session.current ? (
                  <Badge variant="secondary" className="text-xs">This session</Badge>
                ) : (
                  <button
                    type="button"
                    onClick={() => toastSuccess('Session logged out')}
                    className={cn('inline-flex h-7 items-center rounded-pill px-2.5 text-[13px] font-semibold text-error hover:bg-error-bg')}
                  >
                    Revoke
                  </button>
                )}
              </div>
              {index < MOCK_SESSIONS.length - 1 && <Separator />}
            </React.Fragment>
          ))}
        </div>
      </SectionCard>
    </div>
  );
}
